import * as ReactCrop from 'react-image-crop';
import { last } from 'lodash';
import { maxImageMutationUploadBytes, maxImageSizeBytes, maxVideoSizeBytes } from './userApiHelper';
import logger from './logger';

export interface ImageMutation {
  rotation: number;
  backgroundColor: string;
  pixelCrop: ReactCrop.Crop;
  image: HTMLImageElement;
}

export interface DataURLFromFile {
  dataURL: string;
  mimeType?: string;
  width: number;
  height: number;
  filename?: string;
  file?: File;
}

export interface NaturalCropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Map react-image-crop selection to full-resolution pixel coordinates for the API. */
export const cropSelectionToNaturalPixels = (
  percentOrDisplayCrop: ReactCrop.Crop,
  displayPixelCrop: ReactCrop.Crop | undefined,
  image: HTMLImageElement
): NaturalCropRect => {
  const naturalWidth = image.naturalWidth;
  const naturalHeight = image.naturalHeight;
  if (!naturalWidth || !naturalHeight) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  if (percentOrDisplayCrop.unit === '%') {
    return {
      x: Math.round(percentOrDisplayCrop.x * naturalWidth / 100),
      y: Math.round(percentOrDisplayCrop.y * naturalHeight / 100),
      width: Math.round(percentOrDisplayCrop.width * naturalWidth / 100),
      height: Math.round(percentOrDisplayCrop.height * naturalHeight / 100)
    };
  }

  const displayWidth = image.clientWidth || image.width;
  const displayHeight = image.clientHeight || image.height;
  const px = displayPixelCrop || percentOrDisplayCrop;
  if (!displayWidth || !displayHeight) {
    return {
      x: Math.round(px.x),
      y: Math.round(px.y),
      width: Math.round(px.width),
      height: Math.round(px.height)
    };
  }

  return {
    x: Math.round(px.x * naturalWidth / displayWidth),
    y: Math.round(px.y * naturalHeight / displayHeight),
    width: Math.round(px.width * naturalWidth / displayWidth),
    height: Math.round(px.height * naturalHeight / displayHeight)
  };
};

export const revokeDataURLFromFile = (dataURLFromFile?: DataURLFromFile) => {
  if (dataURLFromFile && dataURLFromFile.file && dataURLFromFile.dataURL.indexOf('blob:') === 0) {
    URL.revokeObjectURL(dataURLFromFile.dataURL);
  }
};

export const clearFileInput = (inputID: string) => {
  const input = document.getElementById(inputID) as HTMLInputElement;
  if (!input) {
    logger.error(`clearFileInput: Failed to find file input element ${inputID}.`);
    return;
  }
  input.value = '';
};

// https://stackoverflow.com/a/17412387/1149459
export const applyRotation = async (canvas: HTMLCanvasElement, dataURLFromFile: DataURLFromFile, degrees: number, invertDimensions: boolean = false): Promise<DataURLFromFile> => {
  clearCanvas(canvas);
  const context = canvas.getContext('2d');

  // save the unrotated context of the canvas so we can restore it later
  // the alternative is to untranslate & unrotate after drawing
  if (invertDimensions) {
    const { width, height } = canvas;
    canvas.width = height;
    canvas.height = width;
  }
  context.save();

  // move to the center of the canvas
  context.translate(canvas.width / 2, canvas.height / 2);

  // rotate the canvas to the specified degrees
  context.rotate(degrees * Math.PI / 180);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      // draw the image
      // since the context is rotated, the image will be rotated also
      context.drawImage(image, -image.width / 2, -image.height / 2);

      // we’re done with the rotating so restore the unrotated context
      context.restore();
      const updatedDataURLFromFile: DataURLFromFile = {
        filename: dataURLFromFile.filename,
        mimeType: dataURLFromFile.mimeType,
        width: canvas.width,
        height: canvas.height,
        dataURL: canvas.toDataURL(dataURLFromFile.mimeType)
      };
      resolve(updatedDataURLFromFile);
    };
    image.onerror = reject;
    image.src = dataURLFromFile.dataURL;
  });
};

export const applyBackgroundColor = async (canvas: HTMLCanvasElement, dataURLFromFile: DataURLFromFile, color: string): Promise<DataURLFromFile> => {
  clearCanvas(canvas);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      ctx.drawImage(image, 0, 0);
      const updatedDataURLFromFile: DataURLFromFile = {
        filename: dataURLFromFile.filename,
        mimeType: dataURLFromFile.mimeType,
        width: image.width,
        height: image.height,
        dataURL: canvas.toDataURL(dataURLFromFile.mimeType)
      };
      resolve(updatedDataURLFromFile);
    };
    image.onerror = reject;
    image.src = dataURLFromFile.dataURL;
  });
};

export const applyCrop = async (canvas: HTMLCanvasElement, dataURLFromFile: DataURLFromFile, pixelCrop: ReactCrop.Crop): Promise<DataURLFromFile> => {
  clearCanvas(canvas);
  canvas.width = Math.max(1, Math.round(pixelCrop.width));
  canvas.height = Math.max(1, Math.round(pixelCrop.height));
  const ctx = canvas.getContext('2d');
  const mimeType = (dataURLFromFile.mimeType && dataURLFromFile.mimeType.indexOf('image/') === 0)
    ? dataURLFromFile.mimeType
    : 'image/jpeg';
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        canvas.width,
        canvas.height
      );
      const dataURL = /(jpg|jpeg)/i.test(mimeType)
        ? canvas.toDataURL('image/jpeg', 0.85)
        : canvas.toDataURL(mimeType);
      const outMime = /(jpg|jpeg)/i.test(mimeType) ? 'image/jpeg' : mimeType;
      const updatedDataURLFromFile: DataURLFromFile = {
        filename: dataURLFromFile.filename,
        mimeType: outMime,
        width: canvas.width,
        height: canvas.height,
        dataURL
      };
      resolve(updatedDataURLFromFile);
    };
    image.onerror = reject;
    image.src = dataURLFromFile.dataURL;
  });
};

export const clearCanvas = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext('2d');
  // Store the current transformation matrix
  ctx.save();

  // Use the identity matrix while clearing the canvas
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Restore the transform
  ctx.restore();
};

const isHeicFile = (file: File): boolean => {
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif' ||
    name.endsWith('.heic') || name.endsWith('.heif');
};

export const isHeicMimeType = (mimeType?: string, filename?: string): boolean => {
  const type = (mimeType || '').toLowerCase();
  const name = (filename || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif' ||
    name.endsWith('.heic') || name.endsWith('.heif');
};

const heicMimeForFile = (file: File): string => {
  const type = (file.type || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif') return type;
  return (file.name || '').toLowerCase().endsWith('.heif') ? 'image/heif' : 'image/heic';
};

/** Prefer native decode (no heic2any). Returns null if the browser cannot decode HEIC. */
const tryNativeHeicToJpegFile = async (file: File): Promise<File | null> => {
  try {
    if (typeof createImageBitmap !== 'function') return null;
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return null;
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
    });
    if (!blob) return null;
    const baseName = (file.name || 'photo').replace(/\.(heic|heif)$/i, '') || 'photo';
    return blobToJpegFile(blob, `${baseName}.jpg`);
  } catch (_err) {
    return null;
  }
};

/** Pass HEIC through without conversion — API accepts image/heic|heif. */
const heicFileAsUpload = (file: File): DataURLFromFile => {
  const mimeType = heicMimeForFile(file);
  let uploadFile = file;
  if (!file.type || file.type === 'application/octet-stream') {
    try {
      uploadFile = new File([file], file.name || 'photo.heic', { type: mimeType });
    } catch (_err) {
      uploadFile = file;
    }
  }
  const objectURL = URL.createObjectURL(uploadFile);
  return {
    dataURL: objectURL,
    mimeType,
    width: 0,
    height: 0,
    filename: uploadFile.name || 'photo.heic',
    file: uploadFile
  };
};

type Heic2AnyFn = (options: {
  blob: Blob;
  toType?: string;
  quality?: number;
}) => Promise<Blob | Blob[]>;

let heic2anyLoader: Promise<Heic2AnyFn> | null = null;

/** Load heic2any from a static asset — webpack cannot reliably bundle its UMD/wasm build. */
const loadHeic2Any = (): Promise<Heic2AnyFn> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('HEIC conversion is only available in the browser.'));
  }
  const existing = (window as any).heic2any as Heic2AnyFn | undefined;
  if (typeof existing === 'function') {
    return Promise.resolve(existing);
  }
  if (heic2anyLoader) {
    return heic2anyLoader;
  }
  heic2anyLoader = new Promise<Heic2AnyFn>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = '/assets/heic2any.min.js';
    script.async = true;
    script.onload = () => {
      const fn = (window as any).heic2any as Heic2AnyFn | undefined;
      if (typeof fn === 'function') {
        resolve(fn);
        return;
      }
      heic2anyLoader = null;
      reject(new Error('HEIC converter failed to initialize.'));
    };
    script.onerror = () => {
      heic2anyLoader = null;
      reject(new Error('Could not load HEIC converter.'));
    };
    document.head.appendChild(script);
  });
  return heic2anyLoader;
};

const blobToJpegFile = (blob: Blob, filename: string): File => {
  try {
    return new File([blob], filename, { type: 'image/jpeg' });
  } catch (_err) {
    // Older Safari: File constructor may be unavailable.
    const fallback = blob as File;
    try {
      Object.defineProperty(fallback, 'name', { value: filename });
      Object.defineProperty(fallback, 'type', { value: 'image/jpeg' });
    } catch (_defineErr) {
      // ignore
    }
    return fallback;
  }
};

/** Best-effort HEIC→JPEG via heic2any; returns null on failure (caller may upload HEIC as-is). */
const convertHeicToJpegFile = async (file: File): Promise<File | null> => {
  try {
    const heic2any = await loadHeic2Any();
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    if (!blob) return null;
    const baseName = (file.name || 'photo').replace(/\.(heic|heif)$/i, '') || 'photo';
    return blobToJpegFile(blob, `${baseName}.jpg`);
  } catch (err) {
    logger.error(err instanceof Error ? err : new Error(String(err)));
    return null;
  }
};

/** iPhone .mov often has empty file.type; treat by extension too. */
export const isVideoFile = (file: File | Blob & { name?: string; type?: string }): boolean => {
  const mime = String((file && file.type) || '').toLowerCase();
  if (mime.indexOf('video') >= 0) return true;
  const name = String((file && (file as File).name) || '').toLowerCase();
  return /\.(mp4|mov|m4v|avi|webm|mpeg|mpg)$/i.test(name);
};

const mimeForVideoFile = (file: File): string => {
  const mime = String(file.type || '').toLowerCase();
  if (mime.indexOf('video') >= 0) return mime;
  const name = String(file.name || '').toLowerCase();
  if (name.endsWith('.mov') || name.endsWith('.m4v')) return 'video/quicktime';
  if (name.endsWith('.webm')) return 'video/webm';
  if (name.endsWith('.avi')) return 'video/avi';
  if (name.endsWith('.mpeg') || name.endsWith('.mpg')) return 'video/mpeg';
  return 'video/mp4';
};

export const getDataURLFromFileInput = async (inputID: string): Promise<DataURLFromFile> => {
  const input = document.getElementById(inputID) as HTMLInputElement;
  if (!input) {
    throw new Error(`Failed to find file input element ${inputID}.`);
  }
  let file = input.files && input.files[0];
  if (file) {
    if (isVideoFile(file)) {
      if (file.size > maxVideoSizeBytes) {
        throw new Error('Video must be 50MB or smaller.');
      }
      const objectURL = URL.createObjectURL(file);
      return {
        dataURL: objectURL,
        mimeType: mimeForVideoFile(file),
        width: 0,
        height: 0,
        filename: file.name,
        file
      };
    }
    if (isHeicFile(file)) {
      // Prefer native decode → JPEG when the browser can; otherwise upload HEIC as-is.
      // Do not block the user on heic2any failure.
      const nativeJpeg = await tryNativeHeicToJpegFile(file);
      if (nativeJpeg) {
        file = nativeJpeg;
      } else {
        const converted = await convertHeicToJpegFile(file);
        if (converted) {
          file = converted;
        } else {
          return heicFileAsUpload(file);
        }
      }
    }
    const reader = new FileReader();
    const uploadFile = file;

    return new Promise((resolve, reject) => {
      reader.onload = () => {
        const result = reader.result;
        let dataURL: string;
        if (result instanceof ArrayBuffer) {
          dataURL = String.fromCharCode.apply(null, new Uint16Array(result));
        } else {
          dataURL = result;
        }
        const img = new Image();
        img.onload = async () => {
          const dataURLFromFile = {
            dataURL,
            mimeType: uploadFile.type,
            width: img.width,
            height: img.height,
            filename: uploadFile.name
          };
          resolve(dataURLFromFile);
        };
        img.onerror = reject;
        img.src = dataURL;
      };
      reader.onerror = reject;

      reader.readAsDataURL(uploadFile);
    });
  }
  return;
};

/*export const drawDataURLToCanvas = (canvas: HTMLCanvasElement, dataURLFromFile: DataURLFromFile) => {
  clearCanvas(canvas);
  const ctx = canvas.getContext('2d');
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      canvas.width = dataURLFromFile.width;
      canvas.height = dataURLFromFile.height;
      ctx.drawImage(
        image, 0, 0, dataURLFromFile.width, dataURLFromFile.height
      );
      resolve(canvas.toDataURL(dataURLFromFile.mimeType));
    };
    image.onerror = reject;
    image.src = dataURLFromFile.dataURL;
  });
};*/

const minimumAcceptableWidth = 100;

export const ErrCouldNotCompressFile = new Error('Could not compress file to acceptable size while maintaining minimum width.');

const jpegOutputMimeType = 'image/jpeg';
const jpegQualitySteps = [0.92, 0.85, 0.78, 0.7, 0.62];

const outputMimeForUploadCompression = (mimeType: string) =>
  (mimeType === 'image/png' || mimeType === 'image/webp' ||
    mimeType === 'image/heic' || mimeType === 'image/heif' || isJPG(mimeType))
    ? jpegOutputMimeType : mimeType;

const withUpdatedFilename = (filename: string | undefined, mimeType: string): string | undefined => {
  if (!filename) return filename;
  return filename.replace(/\.\w+$/, getExtensionForMimeType(mimeType));
};

/** Prefer blob: URLs when decoding — large data: URLs can fail on some desktop browsers. */
const loadImageFromDataURLFile = (dataURLFromFile: DataURLFromFile): Promise<{ img: HTMLImageElement; revoke?: () => void }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let revoke: (() => void) | undefined;
    img.onload = () => resolve({ img, revoke });
    img.onerror = reject;
    try {
      if (dataURLFromFile.dataURL && dataURLFromFile.dataURL.indexOf('data:') === 0) {
        const blob = dataURLToBlob(dataURLFromFile);
        const objectURL = URL.createObjectURL(blob);
        revoke = () => URL.revokeObjectURL(objectURL);
        img.src = objectURL;
        return;
      }
    } catch (_err) {
      // Fall through to data URL / existing src.
    }
    img.src = dataURLFromFile.dataURL;
  });
};

export const resizeDataURLToDimensions = (
  dataURLFromFile: DataURLFromFile,
  targetWidth: number,
  targetHeight: number,
  outputMimeType?: string,
  jpegQuality?: number
): Promise<DataURLFromFile> => {
  const mimeType = outputMimeType || dataURLFromFile.mimeType || jpegOutputMimeType;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(targetWidth));
  canvas.height = Math.max(1, Math.floor(targetHeight));
  const ctx = canvas.getContext('2d');
  return loadImageFromDataURLFile(dataURLFromFile).then(({ img, revoke }) => {
    try {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataURL = mimeType === jpegOutputMimeType
        ? canvas.toDataURL(mimeType, jpegQuality != null ? jpegQuality : 0.8)
        : canvas.toDataURL(mimeType);
      return {
        ...dataURLFromFile,
        dataURL,
        mimeType,
        width: canvas.width,
        height: canvas.height,
        filename: withUpdatedFilename(dataURLFromFile.filename, mimeType)
      };
    } finally {
      if (revoke) revoke();
    }
  });
};

/** Shrink photos client-side so /image_mutation requests stay under the API body limit. */
export const ensureDataURLUnderUploadLimit = async (
  dataURLFromFile: DataURLFromFile,
  maxBytes: number = maxImageMutationUploadBytes
): Promise<DataURLFromFile> => {
  if (!dataURLFromFile || dataURLFromFile.mimeType && dataURLFromFile.mimeType.indexOf('video') >= 0) {
    return dataURLFromFile;
  }
  let current = dataURLFromFile;
  if (dataURLBytes(current) <= maxBytes) {
    return current;
  }

  const outputMime = outputMimeForUploadCompression(current.mimeType || jpegOutputMimeType);
  let areaScale = Math.min(0.92, Math.sqrt(maxBytes / Math.max(1, dataURLBytes(current))));
  let qualityIdx = 0;

  while (dataURLBytes(current) > maxBytes) {
    if (current.width <= minimumAcceptableWidth || current.height <= minimumAcceptableWidth) {
      for (const quality of jpegQualitySteps) {
        if (outputMime !== jpegOutputMimeType) {
          break;
        }
        current = await resizeDataURLToDimensions(
          current, current.width, current.height, outputMime, quality
        );
        if (dataURLBytes(current) <= maxBytes) {
          return current;
        }
      }
      throw ErrCouldNotCompressFile;
    }

    const targetWidth = Math.max(minimumAcceptableWidth, Math.floor(current.width * areaScale));
    const targetHeight = Math.max(
      minimumAcceptableWidth,
      Math.floor(current.height * (targetWidth / Math.max(1, current.width)))
    );
    // Drop JPEG quality as we shrink so camera photos reliably land under 1MB.
    const quality = outputMime === jpegOutputMimeType
      ? jpegQualitySteps[Math.min(qualityIdx, jpegQualitySteps.length - 1)]
      : undefined;
    current = await resizeDataURLToDimensions(
      current, targetWidth, targetHeight, outputMime, quality
    );
    areaScale *= 0.82;
    qualityIdx += 1;
  }

  return current;
};

const maxPrepareEdge = 1600;

const inferImageMimeType = (dataURLFromFile: DataURLFromFile): string => {
  const mime = (dataURLFromFile.mimeType || '').toLowerCase();
  if (mime && mime.indexOf('image/') === 0) return mime;
  const name = (dataURLFromFile.filename || '').toLowerCase();
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.gif')) return 'image/gif';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.heic')) return 'image/heic';
  if (name.endsWith('.heif')) return 'image/heif';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.startsWith('pxl_')) return 'image/jpeg';
  if (dataURLFromFile.dataURL && dataURLFromFile.dataURL.indexOf('data:image/') === 0) {
    const match = /^data:(image\/[a-z0-9.+-]+)/i.exec(dataURLFromFile.dataURL);
    if (match) return match[1].toLowerCase();
  }
  return 'image/jpeg';
};

/**
 * Normalize photos before preview/mutation:
 * - Re-encode WebP/HEIC (API cannot decode them) to JPEG
 * - Cap long edge and compress under ~1MB so phone screenshots and camera JPEGs succeed
 * Does not require the /image_mutation API (avoids PNG+1MB server failures).
 */
export const prepareImageForMutation = async (
  dataURLFromFile: DataURLFromFile,
  maxBytes: number = maxImageSizeBytes
): Promise<DataURLFromFile> => {
  if (!dataURLFromFile) return dataURLFromFile;
  const inferredMime = inferImageMimeType(dataURLFromFile);
  if (inferredMime.indexOf('video') >= 0) {
    return dataURLFromFile;
  }

  let current: DataURLFromFile = {
    ...dataURLFromFile,
    mimeType: inferredMime
  };
  const mime = inferredMime;
  const bytes = dataURLBytes(current);
  const width = Math.max(1, current.width || 1);
  const height = Math.max(1, current.height || 1);
  const longEdge = Math.max(width, height);
  const mustReencode =
    mime === 'image/webp' ||
    mime === 'image/heic' ||
    mime === 'image/heif' ||
    mime === 'image/png' ||
    !isJPG(mime) ||
    bytes > maxBytes ||
    longEdge > maxPrepareEdge;

  if (mustReencode) {
    let targetWidth = width;
    let targetHeight = height;
    if (longEdge > maxPrepareEdge) {
      const scale = maxPrepareEdge / longEdge;
      targetWidth = Math.max(1, Math.floor(width * scale));
      targetHeight = Math.max(1, Math.floor(height * scale));
    }
    current = await resizeDataURLToDimensions(
      current, targetWidth, targetHeight, jpegOutputMimeType, 0.8
    );
  }

  return ensureDataURLUnderUploadLimit(current, maxBytes);
};

/** Client-side crop to JPEG (no /image_mutation round trip). */
export const cropImageLocally = async (
  dataURLFromFile: DataURLFromFile,
  pixelCrop: NaturalCropRect | ReactCrop.Crop
): Promise<DataURLFromFile> => {
  const canvas = document.createElement('canvas');
  const crop = {
    x: Math.max(0, Math.round(pixelCrop.x || 0)),
    y: Math.max(0, Math.round(pixelCrop.y || 0)),
    width: Math.max(1, Math.round(pixelCrop.width || 0)),
    height: Math.max(1, Math.round(pixelCrop.height || 0))
  };
  const cropped = await applyCrop(canvas, dataURLFromFile, crop);
  return prepareImageForMutation({
    ...cropped,
    mimeType: jpegOutputMimeType,
    filename: withUpdatedFilename(cropped.filename || dataURLFromFile.filename, jpegOutputMimeType)
  });
};

/** Client-side rotate by degrees (positive = clockwise) then recompress. */
export const rotateImageLocally = async (
  dataURLFromFile: DataURLFromFile,
  degrees: number
): Promise<DataURLFromFile> => {
  const normalized = ((degrees % 360) + 360) % 360;
  if (!normalized) {
    return prepareImageForMutation(dataURLFromFile);
  }
  const canvas = document.createElement('canvas');
  const width = Math.max(1, dataURLFromFile.width || 1);
  const height = Math.max(1, dataURLFromFile.height || 1);
  const swap = normalized === 90 || normalized === 270;
  canvas.width = swap ? height : width;
  canvas.height = swap ? width : height;
  const rotated = await applyRotation(canvas, dataURLFromFile, normalized, false);
  return prepareImageForMutation({
    ...rotated,
    mimeType: jpegOutputMimeType,
    filename: withUpdatedFilename(rotated.filename || dataURLFromFile.filename, jpegOutputMimeType)
  });
};

/*export const lossyCompressUntilAcceptable = async (canvas: HTMLCanvasElement, dataURLFromFile: DataURLFromFile) => {
  let fileSize = dataURLBytes(dataURLFromFile);
  let compressedDataURL = dataURLFromFile;
  while (fileSize > maxImageSizeBytes) {
    if (compressedDataURL.width < minimumAcceptableWidth) {
      throw ErrCouldNotCompressFile;
    }
    const compressionRatio = 1 / (fileSize / maxImageSizeBytes);
    compressedDataURL = await lossyCompressDataURL(canvas, compressedDataURL, compressionRatio);
    fileSize = await dataURLBytes(compressedDataURL);
  }
  return compressedDataURL;
};*/

// This essentially shrinks the dimensions of the photos in order to reduce the file size.
/*export const lossyCompressDataURL = async (canvas: HTMLCanvasElement, dataURLFromFile: DataURLFromFile, areaRatio: number): Promise<DataURLFromFile> => {
  const oneDRatio = Math.sqrt(areaRatio);
  const targetWidth = dataURLFromFile.width * oneDRatio;
  const targetHeight = dataURLFromFile.height * oneDRatio;
  const ctx = canvas.getContext('2d');
  const img = new Image();
  return new Promise((resolve, reject) => {
    img.onload = () => {
      // set size proportional to image
      canvas.height = targetHeight;
      canvas.width = targetWidth;

      // step 1 - resize to 50%
      const oc = document.createElement('canvas');
      const octx = oc.getContext('2d');

      oc.width = targetWidth;
      oc.height = targetHeight;
      octx.drawImage(img, 0, 0, oc.width, oc.height);
      // step 2
      octx.drawImage(oc, 0, 0, targetWidth, targetHeight);

      // step 3, resize to final size
      ctx.drawImage(oc, 0, 0, targetWidth, targetHeight, 0, 0, targetWidth, targetHeight);

      const updatedDataURLFromFile: DataURLFromFile = {
        ...dataURLFromFile,
        width: targetWidth,
        height: targetHeight,
        dataURL: canvas.toDataURL(dataURLFromFile.mimeType)
      };
      resolve(updatedDataURLFromFile);
    };
    img.onerror = reject;
    img.src = dataURLFromFile.dataURL;
  });
};*/

// See https://stackoverflow.com/a/7261048/1149459
export const dataURLToBlob = (dataURLFromFile: DataURLFromFile): Blob => {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  const byteString = atob(dataURLFromFile.dataURL.split(',')[1]);

  // separate out the mime component

  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], {type: dataURLFromFile.mimeType});
};

export const blobToDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result.toString());
    };
    reader.onerror = reject;
    reader.onabort = reject;
    reader.readAsDataURL(blob);
  });
};

export const dataURLBytes = (dataURLFromFile: DataURLFromFile): number => dataURLToBlob(dataURLFromFile).size;

export const isJPG = (mimeType: string) => mimeType.search(/(jpg|jpeg)/i) >= 0;

/*const getMimeTypeFromFromSrc = (src: string) => {
  switch (true) {
    case src.search(/\.(jpg|jpeg)$/) >= 0:
      return 'image/jpeg';
    case src.search(/\.png$/) >= 0:
      return 'image/png';
    case src.search(/\.png$/) >= 0:
      return 'image/gif';
    default:
      return 'text/plain';
  }
};*/
export const getExtensionForMimeType = (mimeType: string) => {
  switch (mimeType) {
    case 'image/jpeg':
      return '.jpg';
    case 'image/png':
      return '.png';
    case 'image/gif':
      return '.gif';
    case 'image/webp':
      return '.webp';
    default:
      logger.error(new Error(`Unexpected image mimetype ${mimeType}.`));
      return '.jpg';
  }
};

/*export const imageSrcToDataURL = (canvas: HTMLCanvasElement, src: string): Promise<DataURLFromFile> => {
  return new Promise((resolve, fail) => {
    const image = new Image();

    image.onload = () => {
      canvas.width = image.naturalWidth; // or 'width' if you want a special/scaled size
      canvas.height = image.naturalHeight; // or 'height' if you want a special/scaled size

      canvas.getContext('2d').drawImage(image, 0, 0);
      const mimeType = getMimeTypeFromFromSrc(src);

      resolve({
        dataURL: canvas.toDataURL(mimeType),
        width: image.width,
        height: image.height,
        filename: last(src.split('/')),
        mimeType
      });
    };
    image.src = src;
    image.onabort = fail;
    image.onerror = fail;
  });
};*/

export const setDataURLDimensions = (dataURLFromFile: DataURLFromFile): Promise<DataURLFromFile> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      dataURLFromFile.width = image.width;
      dataURLFromFile.height = image.height;
      resolve(dataURLFromFile);
    };
    image.onerror = reject;
    image.src = dataURLFromFile.dataURL;
  });
};
